import "./display.css";

// Recessed display — a "well" carved into the panel surface, hosting a
// canvas for live data visualizations (waveform, FFT, ADSR curve, etc.).
//
// design.md L3 treatment: shadow direction inverted (dark from top-left,
// light from bottom-right), content glows ice-blue from within the well.
// The CSS owns the well; this module owns the canvas + DPR scaling and
// hands a 2D context to the consumer's draw function.

export interface DisplayOptions {
  container: HTMLElement;
  /** Desktop / max CSS pixel width. In `fluid` mode this is the max-width
   *  cap; the well shrinks below it on narrower containers. */
  width: number;
  height: number;
  label?: string;
  /** When true, the well's CSS width becomes `min(width, 100%)` and the
   *  raf loop reads the actual rendered width every frame so the canvas
   *  pixel buffer follows container resizes. Use for displays that live
   *  in responsive panel layouts (e.g. inside `LfoModule`). */
  fluid?: boolean;
  /** Render callback invoked every frame the display is "running". The
   * receiver should draw into the supplied context using CSS-pixel
   * coordinates (the canvas is DPR-scaled for you). */
  onFrame?: (ctx: CanvasRenderingContext2D, width: number, height: number) => void;
  /** Show a static frame once. Use instead of `onFrame` for displays whose
   * content doesn't change unless `redraw` is called. */
  onDraw?: (ctx: CanvasRenderingContext2D, width: number, height: number) => void;
}

export class Display {
  private root: HTMLDivElement;
  private well: HTMLDivElement;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private cssW: number;
  private cssH: number;
  private fluid: boolean;
  private onFrame: DisplayOptions["onFrame"] | undefined;
  private onDraw: DisplayOptions["onDraw"] | undefined;
  private rafHandle = 0;
  private running = false;

  constructor(opts: DisplayOptions) {
    this.cssW = opts.width;
    this.cssH = opts.height;
    this.fluid = opts.fluid ?? false;
    if (opts.onFrame) this.onFrame = opts.onFrame;
    if (opts.onDraw) this.onDraw = opts.onDraw;

    this.root = document.createElement("div");
    this.root.className = "display-cell";
    if (this.fluid) this.root.classList.add("is-fluid");

    this.well = document.createElement("div");
    this.well.className = "display-well";
    if (this.fluid) {
      // Mirror the CurveEditor fluid pattern: well stretches to its
      // parent's width up to the requested max. Without `width: 100%`
      // on the wrapping cell the inline-flex root would size-to-content
      // and never grow back when the viewport widens (cf. the fluid-
      // cells-shrink-stuck bug).
      this.well.classList.add("is-fluid");
      this.well.style.width = "100%";
      this.well.style.maxWidth = `${this.cssW}px`;
    } else {
      this.well.style.width = `${this.cssW}px`;
    }
    this.well.style.height = `${this.cssH}px`;

    this.canvas = document.createElement("canvas");
    this.canvas.style.width = this.fluid ? "100%" : `${this.cssW}px`;
    this.canvas.style.height = `${this.cssH}px`;
    const ctx = this.canvas.getContext("2d");
    if (!ctx) throw new Error("display: 2d context unavailable");
    this.ctx = ctx;
    this.well.append(this.canvas);

    this.root.append(this.well);
    if (opts.label) {
      const lbl = document.createElement("span");
      lbl.className = "label";
      lbl.textContent = opts.label;
      this.root.append(lbl);
    }
    opts.container.append(this.root);

    this.scaleForDpr();
    if (this.onDraw) {
      this.redraw();
    } else if (this.onFrame) {
      this.start();
    }
  }

  /** Begin running `onFrame` every animation frame. No-op if no callback. */
  start(): void {
    if (this.running || !this.onFrame) return;
    this.running = true;
    const tick = (): void => {
      if (!this.running) return;
      this.scaleForDpr();
      this.onFrame?.(this.ctx, this.cssW, this.cssH);
      this.rafHandle = requestAnimationFrame(tick);
    };
    this.rafHandle = requestAnimationFrame(tick);
  }

  /** Stop the per-frame loop. The last frame remains visible. */
  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.rafHandle);
  }

  /** Force a single `onDraw` redraw. Use when the source data changed. */
  redraw(): void {
    if (!this.onDraw) return;
    this.scaleForDpr();
    this.onDraw(this.ctx, this.cssW, this.cssH);
  }

  destroy(): void {
    this.stop();
    this.root.remove();
  }

  private scaleForDpr(): void {
    // In fluid mode, the well's rendered width tracks its container, so
    // we read it each frame and resize the canvas pixel buffer to match.
    // The drawFn always receives the *current* CSS width.
    if (this.fluid) {
      const w = this.well.clientWidth;
      if (w > 0 && Math.abs(w - this.cssW) > 0.5) this.cssW = w;
    }
    const dpr = window.devicePixelRatio || 1;
    const px = this.cssW * dpr;
    const py = this.cssH * dpr;
    if (this.canvas.width !== px || this.canvas.height !== py) {
      this.canvas.width = px;
      this.canvas.height = py;
    }
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }
}
